// Creative Strategist → Templates (Task #2380, spec §29–§35).
//
// "[Brand] Creative System": which of the templates this brand may already use
// its creative system actually endorses, which one is the default, and what
// each is good and bad for.
//
// Two views on one route, deep-linked with ?assetType= (the repo's
// search-param convention): the coverage table, and the card gallery for one
// asset type. The asset-type catalog itself comes from
// @shared/creative-strategist-templates — never re-declared here.

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useParams, useSearch } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Copy,
  Eye,
  Layers,
  Loader2,
  MoreHorizontal,
  Plus,
  Star,
  TriangleAlert,
} from "lucide-react";
import { thumbUrl } from "@shared/adImage";
import {
  CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS,
  CS_COVERAGE_LABELS,
  CS_TEMPLATE_RULE_FIELDS,
  CS_TEMPLATE_RULE_LABELS,
  CS_TEMPLATE_RULE_PLACEHOLDERS,
  CS_TEMPLATE_STATUS_CLASSES,
  CS_TEMPLATE_STATUS_HELP,
  CS_TEMPLATE_STATUS_LABELS,
  type CreativeStrategistTemplateRuleField,
} from "@shared/creative-strategist-templates";
import { InlineStringList, InlineTextarea } from "@/components/inline-edit";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  csTemplatesKey,
  type CreativeStrategistCoverageRow,
  type CreativeStrategistTemplateBoard,
  type CreativeStrategistTemplateEntry,
} from "@/lib/creative-strategist";

const COVERAGE_CLASSES: Record<string, string> = {
  ready: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  needs_default: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  needs_approval: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

type StatusAction = "approve" | "approve_default" | "testing" | "retire" | "unreview";

export default function CreativeStrategistTemplatesPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const search = useSearch();
  const { toast } = useToast();

  const assetType = new URLSearchParams(search).get("assetType");
  const templatesKey = csTemplatesKey(brandId);
  const basePath = `/brands/${brandId}/creative-strategist/templates`;

  // Own the queryFn: the asset type rides as a query param (the shared default
  // fn would join it into the path), and the key stays prefixed by the base URL
  // so one invalidation refreshes the coverage view and every gallery.
  const { data: board, isLoading } = useQuery<CreativeStrategistTemplateBoard>({
    queryKey: [templatesKey, assetType ?? "all"],
    enabled: !!brandId,
    queryFn: async () => {
      const url = assetType
        ? `${templatesKey}?assetType=${encodeURIComponent(assetType)}`
        : templatesKey;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error((await res.text()) || res.statusText);
      return (await res.json()) as CreativeStrategistTemplateBoard;
    },
  });

  const [rulesFor, setRulesFor] = useState<CreativeStrategistTemplateEntry | null>(null);
  const [previewFor, setPreviewFor] = useState<CreativeStrategistTemplateEntry | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [templatesKey] });
  const fail = (e: Error) =>
    toast({ title: "That didn't work", description: e.message, variant: "destructive" });

  // Status changes take two shapes: a registry row is patched by id, while an
  // unreviewed candidate is adopted into the registry on its first action.
  const statusMutation = useMutation({
    mutationFn: async (vars: { entry: CreativeStrategistTemplateEntry; action: StatusAction }) => {
      const { entry, action } = vars;
      const res = entry.id
        ? await apiRequest("POST", `/api/creative-strategist/templates/${entry.id}/status`, {
            action,
          })
        : await apiRequest(
            "POST",
            `/api/creative-strategist/brands/${brandId}/templates/status`,
            {
              action,
              assetType: entry.assetType,
              system: entry.system,
              templateType: entry.templateType,
              templateId: entry.templateId,
            },
          );
      return (await res.json()) as { id: string };
    },
    onSuccess: () => invalidate(),
    onError: fail,
  });

  const rulesMutation = useMutation({
    mutationFn: async (vars: { templateId: string; patch: Record<string, unknown> }) => {
      await apiRequest(
        "PATCH",
        `/api/creative-strategist/templates/${vars.templateId}`,
        vars.patch,
      );
    },
    onSuccess: () => invalidate(),
    onError: fail,
  });

  const duplicateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      await apiRequest("POST", `/api/creative-strategist/templates/${templateId}/duplicate`, {});
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Duplicated", description: "The copy starts unreviewed." });
    },
    onError: fail,
  });

  const addMutation = useMutation({
    mutationFn: async (vars: { assetType: string; name: string; notes: string }) => {
      await apiRequest("POST", `/api/creative-strategist/brands/${brandId}/templates`, vars);
    },
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
    },
    onError: fail,
  });

  // Editing rules on a candidate needs a registry row first: "unreview" adopts
  // it into the registry without approving anything.
  const openRules = async (entry: CreativeStrategistTemplateEntry) => {
    if (entry.id) {
      setRulesFor(entry);
      return;
    }
    try {
      const created = await statusMutation.mutateAsync({ entry, action: "unreview" });
      setRulesFor({ ...entry, id: created.id, status: "UNREVIEWED" });
    } catch {
      /* toast already shown */
    }
  };

  const coverage: CreativeStrategistCoverageRow[] = board?.assetTypes ?? [];
  const activeRow = coverage.find((c) => c.assetType === assetType) ?? null;

  const entries = useMemo(() => {
    if (!board || !assetType) return [];
    const order: Record<string, number> = {
      DEFAULT: 0,
      APPROVED: 1,
      TESTING: 2,
      UNREVIEWED: 3,
      RETIRED: 4,
    };
    return board.entries
      .filter((e) => e.assetType === assetType)
      .sort(
        (a, b) =>
          (order[a.status] ?? 9) - (order[b.status] ?? 9) || a.name.localeCompare(b.name),
      );
  }, [board, assetType]);

  // Keep the open dialog in sync with refetched data so a rule edit made in the
  // dialog is reflected the moment it saves.
  const liveRulesEntry = rulesFor
    ? (board?.entries.find((e) => e.id && e.id === rulesFor.id) ?? rulesFor)
    : null;

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!board) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            The creative system for this brand could not be loaded.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      <div className="space-y-3 fade-in-up">
        <button
          type="button"
          onClick={() =>
            navigate(activeRow ? basePath : `/brands/${brandId}/creative-strategist`)
          }
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="link-back-templates"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {activeRow ? "All asset types" : "Creative Strategist"}
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold" data-testid="text-creative-system-title">
              {activeRow ? activeRow.label : `${board.brandName} Creative System`}
            </h1>
            <p className="text-sm text-muted-foreground mt-1" data-testid="text-coverage-summary">
              {activeRow ? activeRow.description : board.summary.text}
            </p>
          </div>
          {activeRow && (
            <Badge
              className={COVERAGE_CLASSES[activeRow.state]}
              data-testid={`badge-coverage-${activeRow.assetType}`}
            >
              {CS_COVERAGE_LABELS[activeRow.state]}
            </Badge>
          )}
        </div>
      </div>

      {!activeRow && (
        <Card className="fade-in-up">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-medium px-4 py-2.5">Asset type</th>
                  <th className="text-left font-medium px-4 py-2.5">Default</th>
                  <th className="text-right font-medium px-4 py-2.5">Approved</th>
                  <th className="text-right font-medium px-4 py-2.5">Testing</th>
                  <th className="text-right font-medium px-4 py-2.5">Coverage</th>
                </tr>
              </thead>
              <tbody>
                {coverage.map((row) => (
                  <tr
                    key={row.assetType}
                    className="border-b last:border-0 hover:bg-muted/40 cursor-pointer transition-colors"
                    onClick={() => navigate(`${basePath}?assetType=${row.assetType}`)}
                    data-testid={`row-asset-type-${row.assetType}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium flex items-center gap-2">
                        {row.label}
                        <ArrowRight className="h-3 w-3 text-muted-foreground/50" />
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS[
                          row.system as keyof typeof CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS
                        ] ?? row.system}
                        {row.candidateCount > 0 && ` · ${row.candidateCount} available`}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-testid={`text-default-${row.assetType}`}>
                      {row.defaultName ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.approvedCount}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.testingCount}</td>
                    <td className="px-4 py-3 text-right">
                      <Badge className={COVERAGE_CLASSES[row.state]}>
                        {CS_COVERAGE_LABELS[row.state]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {activeRow && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground">
              {activeRow.defaultName
                ? `Default: ${activeRow.defaultName}`
                : "No default yet — Creative Strategist will block instead of guessing."}
            </div>
            {/* Adding by hand is only honest where the underlying system has
                no catalog to point at. Everywhere else a template must be one
                of the brand's real, already-permitted templates — approving a
                typed-in name would approve nothing. */}
            {!activeRow.discoverable && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAddOpen(true)}
                data-testid="button-add-template"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add template
              </Button>
            )}
          </div>

          {entries.length === 0 ? (
            <Card>
              <CardContent className="py-14 text-center space-y-2" data-testid="empty-gallery">
                <Layers className="h-6 w-6 mx-auto text-muted-foreground" />
                <div className="text-sm font-medium">Nothing available yet</div>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {activeRow.discoverable
                    ? "This brand has no templates of this kind to review yet. Grant access in the underlying system first — approving here can only narrow what a brand may use."
                    : "This system has no catalog to read, so templates for it are added by hand."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {entries.map((entry) => (
                <TemplateCard
                  key={entry.id ?? `${entry.system}:${entry.templateId}`}
                  entry={entry}
                  busy={statusMutation.isPending}
                  onAction={(action) => statusMutation.mutate({ entry, action })}
                  onEditRules={() => void openRules(entry)}
                  onPreview={() => setPreviewFor(entry)}
                  onDuplicate={() => entry.id && duplicateMutation.mutate(entry.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <RulesDialog
        entry={liveRulesEntry}
        onClose={() => setRulesFor(null)}
        onSave={(patch) =>
          liveRulesEntry?.id
            ? rulesMutation.mutateAsync({ templateId: liveRulesEntry.id, patch })
            : Promise.resolve()
        }
      />

      <PreviewDialog
        entry={previewFor}
        brandName={board.brandName}
        onClose={() => setPreviewFor(null)}
      />

      <AddTemplateDialog
        open={addOpen}
        assetTypeLabel={activeRow?.label ?? ""}
        pending={addMutation.isPending}
        onClose={() => setAddOpen(false)}
        onSubmit={(name, notes) =>
          activeRow && addMutation.mutate({ assetType: activeRow.assetType, name, notes })
        }
      />
    </div>
  );
}

// ── one template card ──────────────────────────────────────────────────────

function TemplateCard({
  entry,
  busy,
  onAction,
  onEditRules,
  onPreview,
  onDuplicate,
}: {
  entry: CreativeStrategistTemplateEntry;
  busy: boolean;
  onAction: (action: StatusAction) => void;
  onEditRules: () => void;
  onPreview: () => void;
  onDuplicate: () => void;
}) {
  const [imageBroken, setImageBroken] = useState(false);
  const preview = entry.previewUrl ? thumbUrl(entry.previewUrl, 480) : null;
  const systemLabel =
    CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS[
      entry.system as keyof typeof CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS
    ] ?? entry.system;

  return (
    <Card className="overflow-hidden flex flex-col" data-testid={`card-template-${entry.id ?? entry.templateId}`}>
      {/* A system with no preview imagery gets a clean typographic card rather
          than a broken image. */}
      {preview && !imageBroken ? (
        <div className="aspect-[4/5] bg-muted">
          <img
            src={preview}
            alt={entry.name}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={() => setImageBroken(true)}
          />
        </div>
      ) : (
        <div className="aspect-[4/5] bg-gradient-to-br from-muted to-muted/40 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="text-base font-semibold leading-snug">{entry.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-2">
              {systemLabel}
            </div>
          </div>
        </div>
      )}

      <CardContent className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" title={entry.name}>
              {entry.name}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {systemLabel}
              {entry.templateType ? ` · ${entry.templateType}` : ""}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {/* Status and the default badge are one chip: `isDefault` mirrors
                the DEFAULT status exactly, so two chips would say "Default"
                twice. The star is what marks the brand's chosen default. */}
            <Badge
              className={`${CS_TEMPLATE_STATUS_CLASSES[entry.status] ?? ""} gap-1`}
              title={CS_TEMPLATE_STATUS_HELP[entry.status] ?? ""}
              data-testid={`badge-status-${entry.id ?? entry.templateId}`}
            >
              {entry.isDefault && <Star className="h-3 w-3" />}
              {CS_TEMPLATE_STATUS_LABELS[entry.status] ?? entry.status}
            </Badge>
          </div>
        </div>

        {!entry.permitted && (
          <div className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
            <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-px" />
            This brand can no longer use the underlying template, so it is never selected.
          </div>
        )}

        {entry.approvedFor.length > 0 && (
          <div className="text-[11px] text-muted-foreground">
            <span className="font-medium text-foreground/70">Best for:</span>{" "}
            {entry.approvedFor.join(", ")}
          </div>
        )}
        {entry.notes && (
          <p className="text-[11px] text-muted-foreground line-clamp-3">{entry.notes}</p>
        )}
        <div className="text-[11px] text-muted-foreground">
          {typeof entry.performanceScore === "number"
            ? `Score ${entry.performanceScore}`
            : "No performance data yet"}
          {entry.usageCount > 0 && ` · used ${entry.usageCount}×`}
        </div>

        <div className="flex items-center gap-1.5 pt-1 mt-auto">
          {entry.status !== "APPROVED" && entry.status !== "DEFAULT" && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={busy || !entry.permitted}
              onClick={() => onAction("approve")}
              data-testid={`button-approve-${entry.id ?? entry.templateId}`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
              Approve
            </Button>
          )}
          {!entry.isDefault && (
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={busy || !entry.permitted}
              onClick={() => onAction("approve_default")}
              data-testid={`button-make-default-${entry.id ?? entry.templateId}`}
            >
              <Star className="h-3 w-3 mr-1" /> Make default
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 ml-auto"
                data-testid={`button-more-${entry.id ?? entry.templateId}`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAction("testing")}>Testing only</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction("retire")}>Retire</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onEditRules}>Edit rules</DropdownMenuItem>
              <DropdownMenuItem onClick={onPreview}>
                <Eye className="h-3.5 w-3.5 mr-2" /> Preview with brand
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!entry.id} onClick={onDuplicate}>
                <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}

// ── rules editor ───────────────────────────────────────────────────────────

function RulesDialog({
  entry,
  onClose,
  onSave,
}: {
  entry: CreativeStrategistTemplateEntry | null;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<unknown>;
}) {
  return (
    <Dialog
      open={!!entry}
      onOpenChange={(open) => {
        if (open) return;
        // Escape and overlay clicks skip blur, which would drop whatever is
        // half-typed in an inline field. Blur first, then close, so the
        // inline editor's own commit-on-blur runs.
        (document.activeElement as HTMLElement | null)?.blur();
        onClose();
      }}
    >
      <DialogContent className="max-w-lg" data-testid="dialog-template-rules">
        <DialogHeader>
          <DialogTitle>{entry?.name ?? "Template rules"}</DialogTitle>
          <DialogDescription>
            What this template is good and bad for, and which parts Creative Strategist may
            change.
          </DialogDescription>
        </DialogHeader>

        {entry && (
          <div className="space-y-4">
            {CS_TEMPLATE_RULE_FIELDS.map((field: CreativeStrategistTemplateRuleField) => (
              <div key={field}>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {CS_TEMPLATE_RULE_LABELS[field]}
                </div>
                <InlineStringList
                  items={entry[field]}
                  variant="chips"
                  tinted
                  placeholder={CS_TEMPLATE_RULE_PLACEHOLDERS[field]}
                  addLabel="Add"
                  testId={`rule-${field}`}
                  onSave={async (next) => {
                    await onSave({ [field]: next });
                  }}
                />
              </div>
            ))}

            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                Notes
              </div>
              <InlineTextarea
                value={entry.notes}
                placeholder="When to reach for this template…"
                testId="template-notes"
                onSave={async (next) => {
                  await onSave({ notes: next });
                }}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── preview with brand ─────────────────────────────────────────────────────

function PreviewDialog({
  entry,
  brandName,
  onClose,
}: {
  entry: CreativeStrategistTemplateEntry | null;
  brandName: string;
  onClose: () => void;
}) {
  const preview = entry?.previewUrl ? thumbUrl(entry.previewUrl, 800) : null;
  return (
    <Dialog open={!!entry} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl" data-testid="dialog-template-preview">
        <DialogHeader>
          <DialogTitle>{entry?.name}</DialogTitle>
          <DialogDescription>
            How {brandName} would use this template.
          </DialogDescription>
        </DialogHeader>

        {entry && (
          <div className="space-y-3">
            {preview ? (
              <img
                src={preview}
                alt={entry.name}
                className="w-full rounded-lg border object-contain max-h-[50vh]"
              />
            ) : (
              <div className="rounded-lg border p-8 text-center">
                <div className="text-lg font-semibold">{entry.name}</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mt-2">
                  {CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS[
                    entry.system as keyof typeof CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS
                  ] ?? entry.system}
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  {entry.description ?? "This system does not carry preview imagery."}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs">
              <RuleRow label="Best for" items={entry.approvedFor} />
              <RuleRow label="Avoid for" items={entry.avoidFor} />
              <RuleRow label="Locked" items={entry.lockedRules} />
              <RuleRow label="Flexible" items={entry.flexibleRules} />
            </div>

            {entry.sourceHref && (
              <DialogFooter>
                <Button variant="outline" size="sm" asChild>
                  <a href={entry.sourceHref} target="_blank" rel="noreferrer">
                    Open in {
                      CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS[
                        entry.system as keyof typeof CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS
                      ] ?? "source"
                    }
                  </a>
                </Button>
              </DialogFooter>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RuleRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5">{items.length ? items.join(", ") : "—"}</div>
    </div>
  );
}

// ── add a template by hand ─────────────────────────────────────────────────

function AddTemplateDialog({
  open,
  assetTypeLabel,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  assetTypeLabel: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (name: string, notes: string) => void;
}) {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
          setName("");
          setNotes("");
        }
      }}
    >
      <DialogContent className="max-w-md" data-testid="dialog-add-template">
        <DialogHeader>
          <DialogTitle>Add a {assetTypeLabel} template</DialogTitle>
          <DialogDescription>
            It starts unreviewed — approve it here before Creative Strategist may use it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Template name"
            data-testid="input-template-name"
          />
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What is it for?"
            rows={3}
            data-testid="input-template-notes"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || pending}
            onClick={() => onSubmit(name.trim(), notes.trim())}
            data-testid="button-save-template"
          >
            {pending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
